import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Pencil, Send, Trash2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { DetailField, SectionCard } from "./shared";
import { formatCurrency, deriveTransactionDetails } from "./LineGrid";
import { ACCOUNTING_BASE } from "./accountingNav";
import VoucherPrintLayout from "./VoucherPrintLayout";
import { useAccountingPermissions } from "./shared";

const VOUCHER_TYPE_LABELS = {
  RECEIPT: "Receipt", PAYMENT: "Payment", CONTRA: "Contra", JOURNAL: "Journal",
  PURCHASE: "Purchase / Vendor Bill", CREDIT_NOTE: "Credit Note", DEBIT_NOTE: "Debit Note",
  ADJUSTMENT: "Adjustment", OPENING: "Opening",
};

// Phase 5 correction (Enforce Payment Nature and Account Consistency):
// professional categories, replacing the earlier vague "Other Payment"
// default. "vendor_payable" is the pre-rename spelling, kept here so
// vouchers saved before this correction still display a correct label
// rather than falling through to a raw enum value.
const PAYMENT_NATURE_LABELS = {
  direct_expense: "Direct Expense", payable_payment: "Payable Payment", vendor_payable: "Payable Payment",
  emergency_expense: "Emergency / No-Invoice Expense", salary_payment: "Salary Payment", reimbursement: "Staff / Member Reimbursement",
  advance_payment: "Advance Payment", capital_payment: "Asset / Capital Payment", refund: "Refund", other: "Other",
};

const PARTY_TYPE_LABELS = { vendor: "Vendor", member: "Member", employee: "Employee / Staff", other: "" };

// Accounting Phase 6: labels for vouchers auto-posted by the Billing<->
// Accounting integration (see server.py _post_bill_to_accounting /
// _post_bill_payment_to_accounting). sourceId for BILL_CREDIT_NOTE is
// "<billId>:<creditNoteId>" so multiple credit notes against one bill never
// collide - split it back apart here purely for display.
const BILLING_SOURCE_LABELS = {
  BILL: "Society Billing — Maintenance Bill",
  BILL_PAYMENT: "Society Billing — Member Payment",
  BILL_CREDIT_NOTE: "Society Billing — Credit Note",
};

function StatusBadge({ status }) {
  const styles = {
    draft: "bg-amber-50 text-amber-700", posted: "bg-emerald-50 text-emerald-700", cancelled: "bg-[#F3F4F6] text-[#6B7280]",
  };
  return <span className={`rounded-sm px-2 py-0.5 text-xs font-semibold capitalize ${styles[status] || "bg-[#F3F4F6] text-[#6B7280]"}`}>{status}</span>;
}

/**
 * Dedicated voucher detail route (/vouchers/:id). Shows the header, every
 * line, and the totals/difference (always Rs 0.00 once posted, since the
 * engine never lets an unbalanced voucher post), plus the status-correct
 * actions: DRAFT -> Edit/Post/Delete, POSTED -> Edit/Delete/Cancel/Reverse,
 * CANCELLED -> read-only (+ Force Delete, testing only).
 *
 * Correction pass ("Edit/Delete Rule"): Edit and Delete are now offered for
 * a POSTED voucher too, not just a draft - but neither is unconditional.
 * The backend checks real downstream dependencies first (already cancelled/
 * reversed, posted automatically from Billing, matched in Bank
 * Reconciliation, or - for a Vendor Bill - would leave that vendor's
 * outstanding overpaid) and returns the exact reason in a 409 if blocked;
 * this page does not pre-guess that client-side, it just surfaces whatever
 * the backend says via the error toast. Delete here is a genuine hard
 * delete (voucher + its ledger lines gone, no trace) - distinct from
 * Cancel/Reverse, which keeps full history via an auto-reversal voucher.
 */
export default function VoucherViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const perms = useAccountingPermissions();
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Phase 15 (Vendor Bills & Payables) item 1: attachments for this voucher,
  // via the existing generic Society attachment system (entityType=voucher,
  // added this phase - see backend society_upload_attachment).
  const [attachments, setAttachments] = useState([]);
  const [documentProfile, setDocumentProfile] = useState(undefined);

  const load = async () => {
    try {
      const res = await api.get(`/society/accounting/engine/vouchers/${id}`);
      setVoucher(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not load this voucher"));
      navigate(`${ACCOUNTING_BASE}/vouchers`, { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/society/accounting/document-profile")
      .then((res) => setDocumentProfile(res.data || {}))
      .catch(() => setDocumentProfile({}));
  }, []);

  // Phase 15 (Vendor Bills & Payables) item 9: "Print" action in the Vendor
  // Bills list rows links here with ?print=1 so it opens straight into the
  // browser print dialog on the existing VoucherPrintLayout, rather than
  // needing a second click once the page has loaded.
  useEffect(() => {
    if (searchParams.get("print") === "1" && voucher && documentProfile !== undefined) {
      const t = setTimeout(() => window.print(), 200);
      return () => clearTimeout(t);
    }
  }, [searchParams, voucher, documentProfile]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/society/attachments", { params: { entityType: "voucher", entityId: id } });
        setAttachments(Array.isArray(res.data) ? res.data : []);
      } catch {
        setAttachments([]);
      }
    })();
  }, [id]);

  const openAttachment = async (attachment) => {
    try {
      const res = await api.get(`/society/attachments/${attachment.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(formatErr(err, "Could not open this attachment"));
    }
  };

  const postVoucher = async () => {
    try {
      setBusy(true);
      await api.post(`/society/accounting/engine/vouchers/${id}/post`);
      toast.success("Voucher posted");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not post this voucher"));
    } finally {
      setBusy(false);
    }
  };

  const deleteDraft = async () => {
    if (!window.confirm("Delete this draft voucher? This cannot be undone.")) return;
    try {
      setBusy(true);
      await api.delete(`/society/accounting/engine/vouchers/${id}`);
      toast.success("Draft deleted");
      navigate(`${ACCOUNTING_BASE}/vouchers`);
    } catch (err) {
      toast.error(formatErr(err, "Could not delete this draft"));
      setBusy(false);
    }
  };

  // Correction pass ("Safe Hard Delete"): a genuine hard delete, distinct
  // from Cancel/Reverse - removes the voucher and its ledger lines
  // completely rather than posting a reversal, so it is only offered when
  // the backend confirms nothing depends on it (see the docstring above).
  const deleteVoucher = async () => {
    if (!window.confirm(`Permanently delete ${voucher.voucherNumber || "this voucher"}? Its accounting entries are removed completely and immediately - unlike Cancel / Reverse, no reversal record is kept. This cannot be undone.`)) return;
    try {
      setBusy(true);
      await api.delete(`/society/accounting/engine/vouchers/${id}`);
      toast.success("Voucher permanently deleted");
      navigate(`${ACCOUNTING_BASE}/vouchers`);
    } catch (err) {
      toast.error(formatErr(err, "Could not delete this voucher"));
    } finally {
      setBusy(false);
    }
  };

  const cancelVoucher = async () => {
    const reason = window.prompt("Cancellation reason (required):", "");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A cancellation reason is required"); return; }
    try {
      setBusy(true);
      await api.post(`/society/accounting/engine/vouchers/${id}/cancel`, { reason: reason.trim() });
      toast.success("Voucher cancelled and reversed");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not cancel this voucher"));
    } finally {
      setBusy(false);
    }
  };

  // Testing/setup-only escape hatch - see BankVoucherFormPage.jsx and the
  // backend endpoint's docstring for the full rationale. Only offered once
  // this voucher is already `cancelled`, never for a live `posted` one.
  const forceDeleteVoucher = async () => {
    const reason = window.prompt(`Permanently delete ${voucher.voucherNumber} and its reversal voucher? This is for removing TEST data only and cannot be undone - it is different from Cancel and leaves no trace. Reason (required):`, "");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    if (!window.confirm(`Really permanently delete ${voucher.voucherNumber}? This cannot be undone.`)) return;
    try {
      setBusy(true);
      await api.delete(`/society/accounting/engine/vouchers/${id}/force-delete`, { params: { reason: reason.trim() } });
      toast.success("Voucher permanently deleted");
      navigate(`${ACCOUNTING_BASE}/vouchers`);
    } catch (err) {
      toast.error(formatErr(err, "Could not delete this voucher"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-[#6B7280]">Loading...</div>;
  if (!voucher) return null;

  const totalDebit = voucher.totalDebit || 0;
  const totalCredit = voucher.totalCredit || 0;
  const difference = Math.round((totalDebit - totalCredit) * 100) / 100;
  const transactionDetails = deriveTransactionDetails(voucher);

  return (
    <>
    {/* Phase 5 "Fix Voucher Printing" correction: the entire on-screen view
        (navigation, action buttons, alerts, admin-switch banner, etc.) is
        excluded from print output via Tailwind's `print:hidden` - only the
        dedicated VoucherPrintLayout below (`hidden print:block`) is ever
        printed. This is the "explicit print-only class" strategy the
        correction requires, instead of a body-wide visibility:hidden rule
        that can leave an ancestor hidden and blank the whole page. */}
    <div className="voucher-screen-only space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link to={`${ACCOUNTING_BASE}/vouchers`} className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6B7280] hover:text-[#111827]">
            &larr; All Vouchers
          </Link>
          <h2 className="mt-1 text-lg font-extrabold text-[#111827]">
            {VOUCHER_TYPE_LABELS[voucher.voucherType] || voucher.voucherType} {voucher.voucherNumber ? `- ${voucher.voucherNumber}` : "(Draft)"}
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* PHASE 19: draft Edit/Delete stay gated on accounting.create
              (matching the backend, which still treats an unposted draft's
              own edit/delete as part of "create" since it has zero
              accounting effect yet - see engine_update_voucher /
              engine_delete_voucher). Posted Edit/Delete require the new
              accounting.edit / accounting.delete permissions. Hiding a
              button here is a UX convenience only; the backend enforces the
              same check regardless of what the UI shows. */}
          {voucher.status === "draft" && (
            <>
              {perms.canCreate && (
                <Button variant="outline" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${id}/edit`)} disabled={busy}><Pencil className="mr-2 h-4 w-4" /> Edit</Button>
              )}
              {perms.canCreate && (
                <Button variant="outline" onClick={deleteDraft} disabled={busy} className="text-red-600"><Trash2 className="mr-2 h-4 w-4" /> Delete Draft</Button>
              )}
              <Button onClick={postVoucher} disabled={busy} className="bg-[#111827] text-white hover:bg-[#374151]"><Send className="mr-2 h-4 w-4" /> Post</Button>
            </>
          )}
          {/* Item 7: drafts are printable too (clearly marked DRAFT / NOT
              POSTED by VoucherPrintLayout) - Print is no longer restricted
              to posted vouchers only. PHASE 19: gated on accounting.print. */}
          {perms.canPrint && (
            <Button variant="outline" onClick={() => window.print()} disabled={busy}>Print</Button>
          )}
          {voucher.status === "posted" && (
            <>
              {perms.canEdit && (
                <Button variant="outline" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${id}/edit`)} disabled={busy}><Pencil className="mr-2 h-4 w-4" /> Edit</Button>
              )}
              {perms.canDelete && (
                <Button variant="outline" onClick={deleteVoucher} disabled={busy} className="text-red-600"><Trash2 className="mr-2 h-4 w-4" /> Delete</Button>
              )}
              {/* Phase 13 (Journal/Contra correction), item 3: Contra and
                  Journal no longer have any Draft/Post/Cancel lifecycle -
                  Edit/Delete above are the complete action set for them.
                  Every other voucher type keeps Cancel/Reverse unchanged. */}
              {!["CONTRA", "JOURNAL"].includes(voucher.voucherType) && (
                <Button variant="outline" onClick={cancelVoucher} disabled={busy} className="text-red-600"><XCircle className="mr-2 h-4 w-4" /> Cancel / Reverse</Button>
              )}
            </>
          )}
          {voucher.status === "cancelled" && (
            <Button variant="outline" onClick={forceDeleteVoucher} disabled={busy} className="text-red-600" title="Testing only: permanently removes this voucher and its reversal">
              <Trash2 className="mr-2 h-4 w-4" /> Force Delete (testing)
            </Button>
          )}
        </div>
      </div>

      {voucher.status === "draft" && (
        <div className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          Draft is not posted to the books. It does not affect any ledger balance until you Post it.
        </div>
      )}

      <SectionCard title="Voucher details">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DetailField label="Voucher Number" value={voucher.voucherNumber || "Assigned on Post"} />
          <DetailField label="Voucher Type" value={VOUCHER_TYPE_LABELS[voucher.voucherType] || voucher.voucherType} />
          <DetailField label="Voucher Date" value={voucher.voucherDate} />
          <DetailField label="Financial Year" value={voucher.financialYearCode || "-"} />
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Status</div>
            <div className="mt-1"><StatusBadge status={voucher.status} /></div>
          </div>
          <DetailField label="Reference Number" value={voucher.referenceNo} />
          <DetailField label="Payment Mode" value={voucher.paymentMode} />
          <DetailField label="Created By" value={voucher.createdBy} />
          <DetailField label="Posted By" value={voucher.postedBy} />
          <DetailField label="Posted At" value={voucher.postedAt ? new Date(voucher.postedAt).toLocaleString() : "-"} />
          {voucher.status === "cancelled" && (
            <>
              <DetailField label="Cancelled By" value={voucher.cancelledBy} />
              <DetailField label="Cancellation Reason" value={voucher.cancelReason} />
            </>
          )}
        </div>
        {/* Phase 15 CORRECTION item 11: for a Vendor Bill, Narration is
            shown once, in the business-friendly "Vendor Bill" section below
            (with Vendor/Invoice/allocations/Paid/Outstanding) rather than
            duplicated here too. Every other voucher type is unaffected. */}
        {voucher.narration && voucher.voucherType !== "PURCHASE" && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Narration</div>
            <p className="mt-1 text-sm text-[#374151]">{voucher.narration}</p>
          </div>
        )}
      </SectionCard>

      {voucher.voucherType === "PURCHASE" && (() => {
        // Phase 15 CORRECTION (Tally-like Purchase / Vendor Bill), item 11:
        // "Show business-friendly layout first ... then show Accounting
        // Entry separately below." Vendor/invoice/allocations/paid/
        // outstanding are all derived straight from this already-loaded
        // voucher - paid is the live sum of paymentAllocations (the same
        // figure the Vendor Bills Outstanding report computes), never a
        // second, separately-cached value.
        const creditLine = voucher.lines?.find((l) => Number(l.credit) > 0);
        const allocationLines = voucher.lines?.filter((l) => Number(l.debit) > 0) || [];
        const paid = (voucher.paymentAllocations || []).reduce((sum, a) => sum + Number(a.amount || 0), 0);
        const billTotal = Number(voucher.totalDebit || 0);
        const outstanding = Math.round((billTotal - paid) * 100) / 100;
        return (
          <SectionCard title="Vendor Bill">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <DetailField label="Vendor" value={creditLine?.vendorLabel || creditLine?.partyName || "-"} />
              <DetailField label="Vendor Invoice No." value={voucher.referenceNo || "-"} />
              <DetailField label="Due Date" value={voucher.dueDate || "-"} />
              <DetailField label="Reference" value={voucher.extraReference || "-"} />
            </div>
            <div className="mt-4">
              <div className="mb-1 text-xs uppercase tracking-[0.14em] text-[#6B7280]">Expense / Asset Allocations</div>
              <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB] text-left text-xs uppercase tracking-[0.1em] text-[#6B7280]">
                      <th className="px-2 py-2">Ledger</th><th className="px-2 py-2">Description</th><th className="px-2 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allocationLines.map((l, i) => (
                      <tr key={l.id || i} className="border-b border-[#F3F4F6]">
                        <td className="px-2 py-1.5">
                          {l.ledgerName}
                          {(l.assetName || l.assetQuantity || l.assetSerialNo || l.assetNotes) && (
                            <div className="mt-0.5 text-xs text-[#6B7280]">
                              {[l.assetName, l.assetQuantity && `Qty ${l.assetQuantity}`, l.assetSerialNo && `S/N ${l.assetSerialNo}`, l.assetNotes].filter(Boolean).join(" · ")}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1.5">{l.narration || "-"}</td>
                        <td className="px-2 py-1.5 text-right">{formatCurrency(l.debit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <DetailField label="Bill Total" value={formatCurrency(billTotal)} />
              <DetailField label="Paid" value={formatCurrency(paid)} />
              <DetailField label="Outstanding" value={formatCurrency(outstanding)} />
            </div>
            {voucher.narration && (
              <div className="mt-4">
                <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Narration</div>
                <p className="mt-1 text-sm text-[#374151]">{voucher.narration}</p>
              </div>
            )}
          </SectionCard>
        );
      })()}

      {BILLING_SOURCE_LABELS[voucher.sourceType] && (
        <SectionCard title="Source">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <DetailField label="Origin" value={BILLING_SOURCE_LABELS[voucher.sourceType]} />
            <DetailField label="Bill / Payment Reference" value={(voucher.sourceId || "").split(":")[0]} />
            <div>
              <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Record</div>
              <div className="mt-1">
                <Link to="/client/society" className="text-sm font-semibold text-[#111827] underline">Open Society Billing</Link>
              </div>
            </div>
          </div>
          <p className="mt-3 text-xs text-[#6B7280]">
            This voucher was posted automatically from Society Billing, not created manually here. The narration above
            includes the originating bill number and member name.
          </p>
        </SectionCard>
      )}

      {Array.isArray(voucher.billAllocations) && voucher.billAllocations.length > 0 && (
        <SectionCard title="Bills Settled">
          <p className="mb-2 text-xs text-[#6B7280]">
            {voucher.sourceType === "MEMBER_RECEIPT" || voucher.voucherType === "RECEIPT"
              ? "Phase 14: this Member Receipt was automatically allocated (oldest first) against this member's own open bills, keeping Billing's own outstanding in sync with this posted entry."
              : "Phase 15: this Payable Payment was automatically allocated (oldest first) against this vendor's own open Vendor Bills."}
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {voucher.billAllocations.map((alloc, i) => {
              const billId = alloc.billVoucherId || alloc.billId;
              const billLabel = alloc.billVoucherNumber || alloc.billNumber || billId;
              return (
                <div key={billId || i} className="rounded-sm border border-[#E5E7EB] px-3 py-2 text-sm">
                  <div className="font-semibold text-[#111827]">
                    {billId ? <Link to={`${ACCOUNTING_BASE}/vouchers/${billId}`} className="hover:underline">{billLabel}</Link> : billLabel}
                  </div>
                  <div className="text-[#6B7280]">{formatCurrency(alloc.amount)} applied</div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}

      {voucher.voucherType === "PURCHASE" && Array.isArray(voucher.paymentAllocations) && voucher.paymentAllocations.length > 0 && (
        <SectionCard title="Payments Received">
          <p className="mb-2 text-xs text-[#6B7280]">
            Phase 15: payments allocated against this Vendor Bill so far. Paid/Balance on the Vendor Bills Outstanding
            report is always computed live from this list, never a separately cached figure.
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {voucher.paymentAllocations.map((alloc, i) => (
              <div key={alloc.paymentVoucherId || i} className="rounded-sm border border-[#E5E7EB] px-3 py-2 text-sm">
                <div className="font-semibold text-[#111827]">
                  {alloc.paymentVoucherId ? (
                    <Link to={`${ACCOUNTING_BASE}/vouchers/${alloc.paymentVoucherId}`} className="hover:underline">{alloc.paymentVoucherNumber || alloc.paymentVoucherId}</Link>
                  ) : (alloc.paymentVoucherNumber || "-")}
                </div>
                <div className="text-[#6B7280]">{formatCurrency(alloc.amount)} allocated</div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {attachments.length > 0 && (
        <SectionCard title="Attachments">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {attachments.map((a) => (
              <button
                key={a.id} type="button" onClick={() => openAttachment(a)}
                className="rounded-sm border border-[#E5E7EB] px-3 py-2 text-left text-sm hover:border-[#111827]"
              >
                <div className="font-semibold text-[#111827] hover:underline">{a.label || a.fileName || "Attachment"}</div>
                <div className="text-xs text-[#6B7280]">{a.createdAt ? new Date(a.createdAt).toLocaleString() : ""}</div>
              </button>
            ))}
          </div>
        </SectionCard>
      )}

      {transactionDetails && (
        <SectionCard title="Transaction Details">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="lg:col-span-1">
              <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Amount</div>
              <div className="mt-1 text-2xl font-extrabold text-[#111827]">{formatCurrency(transactionDetails.amount)}</div>
            </div>
            {voucher.voucherType === "PAYMENT" ? (
              // Exact field order per the Phase 5 correction (item 8): Amount, Paid To,
              // Payment Nature, Payment For, Paid From, Payment Mode, Reference, Narration.
              <>
                <DetailField label={transactionDetails.rows[0].label} value={transactionDetails.rows[0].value} />
                <DetailField label="Payment Nature" value={PAYMENT_NATURE_LABELS[voucher.paymentNature] || "Other"} />
                <DetailField label={transactionDetails.rows[1].label} value={transactionDetails.rows[1].value} />
                <DetailField label={transactionDetails.rows[2].label} value={transactionDetails.rows[2].value} />
              </>
            ) : (
              transactionDetails.rows.map((row) => <DetailField key={row.label} label={row.label} value={row.value} />)
            )}
            {/* Phase 13: Contra now captures Payment Mode too (Transfer via
                Bank Transfer / Cheque / etc.), so it is no longer excluded
                here - only voucher types that never ask for it (Journal and
                beyond, which don't reach this branch at all since
                deriveTransactionDetails returns null for them) skip it. */}
            <DetailField label="Payment Mode" value={voucher.paymentMode} />
            <DetailField label="Reference Number" value={voucher.referenceNo} />
          </div>
        </SectionCard>
      )}

      <SectionCard title="Accounting Entry">
        <p className="mb-2 text-xs text-[#6B7280]">The full double-entry booking for this voucher - always shows the real ledger name on both sides, for accounting/audit purposes.</p>
        <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB] text-left text-xs uppercase tracking-[0.1em] text-[#6B7280]">
                <th className="px-2 py-2">Ledger</th>
                <th className="px-2 py-2 text-right">Debit</th>
                <th className="px-2 py-2 text-right">Credit</th>
                <th className="px-2 py-2">Party / Member / Vendor</th>
                <th className="px-2 py-2">Reference</th>
                <th className="px-2 py-2">Narration</th>
              </tr>
            </thead>
            <tbody>
              {(voucher.lines || []).map((line) => (
                <tr key={line.id} className="border-b border-[#F3F4F6]">
                  <td className="px-2 py-1.5 font-semibold text-[#111827]">{line.ledgerName || line.bankAccountLabel || line.vendorLabel}</td>
                  <td className="px-2 py-1.5 text-right">{line.debit > 0 ? formatCurrency(line.debit) : "-"}</td>
                  <td className="px-2 py-1.5 text-right">{line.credit > 0 ? formatCurrency(line.credit) : "-"}</td>
                  <td className="px-2 py-1.5">{line.vendorLabel || line.partyName || "-"}</td>
                  <td className="px-2 py-1.5">{line.referenceNo || "-"}</td>
                  <td className="px-2 py-1.5">{line.narration || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-6 rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm">
          <div>Total Debit: <span className="font-semibold text-[#111827]">{formatCurrency(totalDebit)}</span></div>
          <div>Total Credit: <span className="font-semibold text-[#111827]">{formatCurrency(totalCredit)}</span></div>
          <div>Difference: <span className={`font-semibold ${difference === 0 ? "text-emerald-600" : "text-red-600"}`}>{formatCurrency(Math.abs(difference))}</span></div>
        </div>
      </SectionCard>
    </div>

    {/* Print-only: hidden on screen, shown only inside @media print. This is
        the one and only thing that reaches paper. Plain hand-authored CSS
        classes (.voucher-screen-only / .voucher-print-only in index.css),
        not Tailwind's print: variant - guaranteed present in the CSS bundle
        regardless of JIT/purge timing, and paired with an explicit
        `visibility: visible !important` override for #voucher-print-area so
        it is never caught by the pre-existing global
        `body * { visibility: hidden }` print rule used elsewhere in the
        app (see index.css for the full explanation). */}
    <div className="voucher-print-only">
      <VoucherPrintLayout
        voucher={voucher}
        societyName={documentProfile?.societyName}
        societyAddress={documentProfile?.address}
        registrationNumber={documentProfile?.registrationNumber}
      />
    </div>
    </>
  );
}
